import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Button,
  Divider,
  Box,
  TextField,
  MenuItem,
  CircularProgress,
  Paper,
  Chip,
  useTheme,
  alpha,
  Fade,
  Slide,
  Snackbar,
  Alert,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  Pencil,
  User,
  Briefcase,
  Phone,
  Mail,
  CreditCard,
  Building,
  MapPin,
  UserCheck,
  SaveIcon,
  XIcon,
} from "lucide-react";
import InputMask from "inputmask";
import { useDispatch, useSelector } from "react-redux";
import { phone } from "phone";
import { getUserProfileApi, updateUserProfileApi } from "../services/api/userApi";

const formatValue = (value) => {
  if (!value) return "-";
  return value
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const ICON_COLOR = "#303f9f";

const ProfileDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const cnicRef = useRef(null);
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const validatePhoneNumber = (inputNumber) => {
    if (!inputNumber) return "Phone Number is required";

    const { isValid } = phone(inputNumber, {
      country: "pk",
    });
    return isValid ? true : "Invalid phone number format";
  };

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ shouldFocusError: false });

  useEffect(() => {
    if (userInfo) {
      reset({
        name: userInfo?.name || "",
        email: userInfo.email || "",
        phoneNumber: userInfo.phoneNumber || "",
        cnic: userInfo.cnic || "",
        userType: userInfo.userType || "Unknown",
        organization: userInfo.organization || "",
        designation: userInfo.designation || "",
        officeAddress: userInfo.officeAddress || "",
        barCouncil: userInfo.barCouncil || "N/A",
      });
    }
  }, [userInfo, reset]);

  const handleSave = async (formData) => {
    try {
      setLoading(true);
      const response = await updateUserProfileApi({ ...formData, userId: userInfo._id });

      if (response.status === 200) {
        dispatch({
          type: "USER_LOGIN_SUCCESS",
          payload: response.data,
        });
        setSnackbarMessage("Profile updated successfully!");
        setSnackbarOpen(true);
        setEditMode(false);
      }
    } catch (error) {
      setSnackbarMessage("Error updating profile. Please try again.");
      setSnackbarOpen(true);
      console.error("Profile update error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const ProfileField = ({ icon, label, value }) => (
    <Box
      sx={{
        display: "flex",
        mb: 2,
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          mr: 1.5,
          color: ICON_COLOR,
          mt: 0.5,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 0.5 }}
        >
          {label}
        </Typography>
        <Typography variant="body2" fontWeight="medium">
          {value || "N/A"}
        </Typography>
      </Box>
    </Box>
  );

  useEffect(() => {
    fetchUserProfile();
  }, [dispatch, userInfo?._id]);

  const fetchUserProfile = async () => {
    try {
      if (!userInfo?._id) {
        console.warn("User ID not available.");
        return;
      }

      const response = await getUserProfileApi(userInfo._id);

      if (response.status === 200) {
        const data = response.data;
        dispatch({
          type: "USER_LOGIN_SUCCESS",
          payload: data,
        });
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  useEffect(() => {
    if (cnicRef.current) {
      InputMask({ mask: "99999-9999999-9" }).mask(cnicRef.current);
    }
  }, []);

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          onClose();
          reset(userInfo);
          setEditMode(false);
        }}
        maxWidth="md"
        fullWidth
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" }}
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: 24,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.default,
            p: 2,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              <Box>
                <Typography variant="h6" fontWeight="600">
                  {editMode ? "Edit Profile" : "User Profile"}
                </Typography>
                {!editMode && (
                  <Typography variant="body2" color="text.secondary">
                    {userInfo.name}{" "}
                  </Typography>
                )}
              </Box>
            </Box>

            {!editMode ? (
              <Button
                variant="outlined"
                startIcon={<Pencil size={16} color={ICON_COLOR} />}
                onClick={() => setEditMode(true)}
                size="small"
                sx={{
                  textTransform: "none",
                  borderRadius: "24px",
                  padding: "6px 16px",
                  borderColor: ICON_COLOR,
                  color: ICON_COLOR,
                  "&:hover": {
                    borderColor: ICON_COLOR,
                    backgroundColor: alpha(ICON_COLOR, 0.05),
                  },
                }}
              >
                Edit
              </Button>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignContent: "center",
                  alignItems: "center",
                }}
              >
                <Button
                  variant="text"
                  onClick={handleSubmit(handleSave)}
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon
                        size={16}
                        style={{ marginBottom: "3px" }}
                        fontSize="small"
                      />
                    )
                  }
                  sx={{
                    bgcolor: alpha(ICON_COLOR, 0.1),
                    color: ICON_COLOR,
                    "&:hover": {
                      bgcolor: alpha(ICON_COLOR, 0.2),
                      transform: "translateY(-2px)",
                    },
                    textTransform: "capitalize",
                    transition: "all 0.2s ease",
                    borderRadius: "8px",
                    padding: "6px 12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "36px",
                  }}
                >
                  Save
                </Button>
                <Button
                  variant="text"
                  color="error"
                  onClick={() => {
                    reset(userInfo);
                    setEditMode(false);
                  }}
                  disabled={loading}
                  startIcon={
                    <XIcon
                      size={16}
                      style={{ marginBottom: "3px" }}
                      fontSize="small"
                    />
                  }
                  sx={{
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    color: theme.palette.error.main,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.error.main, 0.2),
                      transform: "translateY(-2px)",
                    },
                    textTransform: "capitalize",
                    transition: "all 0.2s ease",
                    borderRadius: "8px",
                    padding: "6px 12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "36px",
                  }}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: alpha(ICON_COLOR, 0.2),
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.background.default,
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Grid container spacing={0}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                    <User size={18} color={ICON_COLOR} />
                    <Typography
                      variant="subtitle1"
                      fontWeight="600"
                      sx={{ ml: 1 }}
                    >
                      Personal Information
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {editMode ? (
                    <Fade in={editMode}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="name"
                            control={control}
                            rules={{ required: "Full Name is required" }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                disabled={loading}
                                label="Full Name"
                                fullWidth
                                size="small"
                                error={!!errors.name}
                                {...register("name", {
                                  required: "Full Name is required",
                                })}
                                helperText={errors.name?.message}
                                FormHelperTextProps={{
                                  sx: { fontSize: "10px", marginTop: "2px" },
                                }}
                                InputProps={{
                                  sx: {
                                    bgcolor: "white",
                                    fontSize: { xs: "12px", sm: "14px" },
                                    "& input:-webkit-autofill": {
                                      backgroundColor: "white !important",
                                      WebkitBoxShadow: "0 0 0 30px white inset !important",
                                      WebkitTextFillColor: "black !important",
                                    },
                                  },
                                }}
                                InputLabelProps={{ sx: { fontSize: "14px" } }}
                                required
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Email"
                            variant="outlined"
                            type="email"
                            {...register("email", {
                              required: "Email is required",
                              pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: "Invalid email address",
                              },
                            })}
                            value={userInfo.email}
                            disabled
                            FormHelperTextProps={{ sx: { fontSize: "10px", marginTop: "2px" } }}
                            InputProps={{
                              sx: {
                                bgcolor: "white",
                                fontSize: { xs: "12px", sm: "14px" },
                                "& input:-webkit-autofill": {
                                  backgroundColor: "white !important",
                                  WebkitBoxShadow: "0 0 0 30px white inset !important",
                                  WebkitTextFillColor: "black !important",
                                },
                              },
                            }}
                            InputLabelProps={{ sx: { fontSize: "14px" } }}
                            required
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Controller
                            name="phoneNumber"
                            control={control}
                            rules={{
                              required: "Phone Number is required",
                              validate: (value) => validatePhoneNumber(value),
                              pattern: {
                                value: /^[0-9\s+()-]+$/,
                                message: "Please enter a valid phone number",
                              },
                            }}
                            render={({ field }) => (
                              <>
                                <PhoneInput
                                  {...field}
                                  country={"pk"}
                                  disabled={loading}
                                  disableCountryCode={false}
                                  disableSearchIcon
                                  enableSearch={true}
                                  placeholder="Enter phone number *"
                                  value={field.value}
                                  onChange={field.onChange}
                                  inputProps={{ required: true, autoFocus: true }}
                                  inputStyle={{
                                    width: "100%",
                                    height: "36px",
                                    fontSize: { xs: "12px", sm: "14px" },
                                    backgroundColor: "white",
                                    borderColor: errors.phoneNumber ? "red" : "#ced4da",
                                    borderRadius: "4px",
                                    paddingLeft: "48px",
                                  }}
                                  buttonStyle={{
                                    borderColor: errors.phoneNumber ? "red" : "#ced4da",
                                    backgroundColor: "white",
                                  }}
                                  containerStyle={{ width: "100%" }}
                                />
                                {errors.phoneNumber && (
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{ mt: 0.5, display: "block" }}
                                  >
                                    {errors.phoneNumber.message}
                                  </Typography>
                                )}
                              </>
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            inputRef={cnicRef}
                            autoComplete="off"
                            size="small"
                            disabled={loading}
                            fullWidth
                            label="CNIC"
                            variant="outlined"
                            {...register("cnic", {
                              required: "CNIC is required",
                              pattern: {
                                value: /^\d{5}-\d{7}-\d{1}$/,
                                message: "Please enter a valid CNIC (e.g., 12345-1234567-1)",
                              },
                            })}
                            error={!!errors.cnic}
                            helperText={errors.cnic?.message}
                            placeholder="XXXXX-XXXXXXX-X"
                            FormHelperTextProps={{ sx: { fontSize: "10px", marginTop: "2px" } }}
                            InputProps={{
                              sx: {
                                bgcolor: "white",
                                fontSize: "14px",
                                "& input:-webkit-autofill": {
                                  backgroundColor: "white !important",
                                  WebkitBoxShadow: "0 0 0 30px white inset !important",
                                  WebkitTextFillColor: "black !important",
                                },
                              },
                            }}
                            InputLabelProps={{ sx: { fontSize: "14px" } }}
                            required
                          />
                        </Grid>
                      </Grid>
                    </Fade>
                  ) : (
                    <Fade in={!editMode}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <ProfileField icon={<User size={18} color={ICON_COLOR} />} label="Full Name" value={userInfo?.name} />
                          <ProfileField icon={<Mail size={18} color={ICON_COLOR} />} label="Email Address" value={userInfo.email} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <ProfileField icon={<Phone size={18} color={ICON_COLOR} />} label="Phone Number" value={userInfo.phoneNumber} />
                          <ProfileField icon={<CreditCard size={18} color={ICON_COLOR} />} label="CNIC" value={userInfo.cnic} />
                        </Grid>
                      </Grid>
                    </Fade>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                    <Briefcase size={18} color={ICON_COLOR} />
                    <Typography variant="subtitle1" fontWeight="600" sx={{ ml: 1 }}>
                      Professional Information
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {editMode ? (
                    <Fade in={editMode}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="userType"
                            control={control}
                            rules={{ required: "User Type is required" }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                size="small"
                                fullWidth
                                label="User Type"
                                disabled={loading}
                                select
                                variant="outlined"
                                error={!!errors.userType}
                                helperText={errors.userType?.message}
                                FormHelperTextProps={{ sx: { fontSize: "10px", marginTop: "2px" } }}
                                InputProps={{ sx: { bgcolor: "white", fontSize: { xs: "12px", sm: "14px" } } }}
                                InputLabelProps={{ sx: { fontSize: "14px" } }}
                                SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 200, "& .MuiMenuItem-root": { fontSize: "14px" } } } } }}
                                required
                              >
                                <MenuItem value="legal-practitioner">Legal Practitioner</MenuItem>
                                <MenuItem value="law-student">Law Student</MenuItem>
                                <MenuItem value="researcher">Researcher</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                              </TextField>
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Controller
                            name="barCouncil"
                            control={control}
                            rules={{ required: "Bar Council is required" }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                size="small"
                                fullWidth
                                label="Bar Council"
                                select
                                disabled={loading}
                                variant="outlined"
                                error={!!errors.barCouncil}
                                helperText={errors.barCouncil?.message}
                                FormHelperTextProps={{ sx: { fontSize: "10px", marginTop: "2px" } }}
                                InputProps={{ sx: { bgcolor: "white", fontSize: { xs: "12px", sm: "14px" } } }}
                                InputLabelProps={{ sx: { fontSize: "14px" } }}
                                SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 200, "& .MuiMenuItem-root": { fontSize: "14px" } } } } }}
                                required
                              >
                                <MenuItem value="N/A">N/A</MenuItem>
                                <MenuItem value="pakistan-bar-council">Pakistan Bar Council</MenuItem>
                                <MenuItem value="punjab-bar-council">Punjab Bar Council</MenuItem>
                                <MenuItem value="sindh-bar-council">Sindh Bar Council</MenuItem>
                                <MenuItem value="khyber-pakhtunkhwa-bar-council">KPK Bar Council</MenuItem>
                                <MenuItem value="islamabad-bar-council">Islamabad Bar Council</MenuItem>
                              </TextField>
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Controller
                            name="organization"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                size="small"
                                fullWidth
                                disabled={loading}
                                label="Organization (optional)"
                                variant="outlined"
                                {...register("organization")}
                                InputProps={{ sx: { bgcolor: "white", fontSize: { xs: "12px", sm: "14px" } } }}
                                InputLabelProps={{ sx: { fontSize: "14px" } }}
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Controller
                            name="designation"
                            control={control}
                            rules={{ required: "Designation is required" }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                size="small"
                                fullWidth
                                disabled={loading}
                                required
                                label="Designation"
                                variant="outlined"
                                error={!!errors.designation}
                                helperText={errors.designation?.message}
                                FormHelperTextProps={{ sx: { fontSize: "10px", marginTop: "2px" } }}
                                InputProps={{ sx: { bgcolor: "white", fontSize: { xs: "12px", sm: "14px" } } }}
                                InputLabelProps={{ sx: { fontSize: "14px" } }}
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Controller
                            name="officeAddress"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                size="small"
                                fullWidth
                                disabled={loading}
                                label="Office Address (optional)"
                                variant="outlined"
                                multiline
                                rows={2}
                                InputProps={{ sx: { bgcolor: "white", fontSize: { xs: "12px", sm: "14px" } } }}
                                InputLabelProps={{ sx: { fontSize: "14px" } }}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Fade>
                  ) : (
                    <Fade in={!editMode}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                              User Type
                            </Typography>
                            <Chip
                              label={formatValue(userInfo.userType)}
                              size="small"
                              sx={{ borderRadius: 1, bgcolor: alpha(ICON_COLOR, 0.1), color: ICON_COLOR, fontWeight: 500 }}
                            />
                          </Box>
                          <ProfileField icon={<Building size={18} color={ICON_COLOR} />} label="Organization" value={userInfo.organization} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                              Bar Council
                            </Typography>
                            <Chip
                              label={formatValue(userInfo.barCouncil)}
                              size="small"
                              sx={{ borderRadius: 1, bgcolor: alpha(ICON_COLOR, 0.1), color: ICON_COLOR, fontWeight: 500 }}
                            />
                          </Box>
                          <ProfileField icon={<UserCheck size={18} color={ICON_COLOR} />} label="Designation" value={userInfo.designation} />
                        </Grid>
                        <Grid item xs={12}>
                          <ProfileField icon={<MapPin size={18} color={ICON_COLOR} />} label="Office Address" value={userInfo.officeAddress} />
                        </Grid>
                      </Grid>
                    </Fade>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            p: 1.5,
            bgcolor: theme.palette.background.default,
          }}
        >
          <Button
            onClick={() => {
              onClose();
              reset(userInfo);
              setEditMode(false);
            }}
            sx={{
              borderRadius: "24px",
              padding: "8px 24px",
              textTransform: "none",
              fontWeight: "bold",
              borderColor: ICON_COLOR,
              color: ICON_COLOR,
              "&:hover": {
                borderColor: ICON_COLOR,
                backgroundColor: alpha(ICON_COLOR, 0.05),
              },
            }}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{ width: "100%", borderRadius: 2, bgcolor: ICON_COLOR }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProfileDialog;
